import React from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Grid, List, FolderPlus, Upload, Globe } from 'lucide-react';

interface FilesControlsProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  // Action button props
  onCreateFolder: () => void;
  onUploadFile: () => void;
  onAddExternalUrl: () => void;
}

const FilesControls: React.FC<FilesControlsProps> = React.memo(({
  searchTerm,
  onSearchChange,
  viewMode,
  onViewModeChange,
  onCreateFolder,
  onUploadFile,
  onAddExternalUrl,
}) => {
  return (
    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      {/* Search */}
      <div className="relative max-w-md flex-1">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Αναζήτηση αρχείων..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white py-3 pr-4 pl-10 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
        />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {/* Action Buttons */}
        <motion.button
          onClick={onCreateFolder}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
          title="Δημιουργία νέου φακέλου"
        >
          <FolderPlus className="h-4 w-4" />
          <span className="hidden sm:inline">Φάκελος</span>
        </motion.button>

        <motion.button
          onClick={onUploadFile}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 shadow-sm hover:border-green-300 hover:bg-green-50 hover:text-green-600"
          title="Μεταφόρτωση αρχείου"
        >
          <Upload className="h-4 w-4" />
          <span className="hidden sm:inline">Αρχείο</span>
        </motion.button>

        <motion.button
          onClick={onAddExternalUrl}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 shadow-sm hover:border-purple-300 hover:bg-purple-50 hover:text-purple-600"
          title="Προσθήκη εξωτερικού συνδέσμου"
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">Σύνδεσμος</span>
        </motion.button>

        {/* Divider */}
        <div className="h-6 w-px bg-gray-200" />

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 shadow-sm hover:border-gray-300 hover:bg-gray-50"
        >
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline">Φίλτρο</span>
        </motion.button>

        {/* View Mode Toggle */}
        <div className="flex rounded-lg border border-gray-200 bg-white shadow-sm">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`flex items-center justify-center rounded-l-lg p-2 transition-all ${
              viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
            }`}
            title="Προβολή πλέγματος"
          >
            <Grid className="h-4 w-4" />
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`flex items-center justify-center rounded-r-lg p-2 transition-all ${
              viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
            }`}
            title="Προβολή λίστας"
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
});

FilesControls.displayName = 'FilesControls';

export default FilesControls;
